import { Fragment, useEffect, useMemo, useRef } from 'react';

import {
  DataTableDetailPanel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TooltipProvider,
  useDataTable
} from 'design-system';

import { TABLE_ROW_HEIGHT_PX } from '@/config/constants';
import { Permission } from '@/entities/permissions';
import { SymbolsPreview } from '@/features/_shared';
import { DateTextVariants, useDateText, usePermission } from '@/features/_shared/user';

import {
  collectMultiGroupColumns,
  getMultiGroupTableColumns,
  type MultiGroupTableRow
} from '../multiGroupTableColumns';

import type { MultiGroupTableSectionType } from '../../../types';

type MultiGroupTableTabProps = {
  sections: MultiGroupTableSectionType[];
  height?: number;
  isLoading?: boolean;
};

const getMaxHeight = (height: number | undefined) =>
  height ? `${height * TABLE_ROW_HEIGHT_PX + 47 - TABLE_ROW_HEIGHT_PX / 1.5}px` : 'fit-content';

const getAlignmentClass = (align: 'start' | 'center' | 'end' | undefined) => {
  if (align === 'end') return 'text-right';
  if (align === 'center') return 'text-center';
  return undefined;
};

const MultiGroupTableTab = ({ sections, height, isLoading = false }: MultiGroupTableTabProps) => {
  const { hasPermission } = usePermission();
  const { formatDateString } = useDateText();
  const isMarketRestricted = !hasPermission(Permission.ViewMarketScreens);

  const rows = useMemo<MultiGroupTableRow[]>(
    () =>
      sections.map((section, sectionIndex) => ({
        kind: 'section',
        id: `section:${sectionIndex}:${section.name}`,
        name: section.name,
        subRows: section.rows.map((value, rowIndex) => {
          const hasAnyValue =
            Boolean(value.last) || Boolean(value.bValues && Object.keys(value.bValues).length > 0);

          return {
            kind: 'multiGroup',
            id: `multi-group:${sectionIndex}:${rowIndex}:${value.symbolA.key.Code}:${value.symbolB.key.Code}`,
            value,
            restricted: isMarketRestricted || !hasAnyValue
          };
        })
      })),
    [isMarketRestricted, sections]
  );
  const properties = useMemo(() => collectMultiGroupColumns(sections), [sections]);
  const columns = useMemo(
    () =>
      getMultiGroupTableColumns({
        formatDate: date => formatDateString(date, DateTextVariants.DAY_MONTH),
        isLoading,
        properties
      }),
    [formatDateString, isLoading, properties]
  );

  const table = useDataTable({
    columns,
    data: rows,
    getRowCanExpand: row =>
      row.original.kind === 'section' ? row.original.subRows.length > 0 : !row.original.restricted,
    getRowId: row => row.id,
    getSubRows: row => (row.kind === 'section' ? row.subRows : undefined)
  });
  const initializedSectionIds = useRef(new Set<string>());
  useEffect(() => {
    const newSectionIds = rows
      .map(row => row.id)
      .filter(rowId => !initializedSectionIds.current.has(rowId));
    if (newSectionIds.length === 0) return;
    newSectionIds.forEach(rowId => initializedSectionIds.current.add(rowId));

    table.setExpanded(current => {
      const next = current === true ? {} : { ...current };
      let changed = current === true;
      for (const rowId of newSectionIds) {
        if (next[rowId] === undefined) {
          next[rowId] = true;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [rows, table]);
  const visibleRows = table.getRowModel().rows;

  return (
    <TooltipProvider>
      <section
        aria-label='Multi-group table scroll area'
        className='w-full overflow-auto'
        style={{ maxHeight: getMaxHeight(height) }}
      >
        <Table aria-label='Multi-group table'>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead
                    key={header.id}
                    className={getAlignmentClass(header.column.columnDef.meta?.align)}
                  >
                    {header.isPlaceholder ? null : <table.FlexRender header={header} />}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {visibleRows.map(row => {
              const dataRow = row.original.kind === 'multiGroup' ? row.original : null;

              return (
                <Fragment key={row.id}>
                  <TableRow
                    className={dataRow?.restricted ? 'bg-muted! text-muted-foreground' : undefined}
                    data-tone={dataRow?.restricted ? 'restricted' : undefined}
                  >
                    {row.getVisibleCells().map(cell => (
                      <TableCell
                        key={cell.id}
                        className={getAlignmentClass(cell.column.columnDef.meta?.align)}
                      >
                        <table.FlexRender cell={cell} />
                      </TableCell>
                    ))}
                  </TableRow>
                  {dataRow ? (
                    <DataTableDetailPanel
                      colSpan={row.getVisibleCells().length}
                      row={row}
                    >
                      <SymbolsPreview
                        enableQuery
                        hideInfo={false}
                        items={[
                          {
                            symbol: dataRow.value.symbolA.key,
                            label: 'Last',
                            metadata: dataRow.value.symbolAMetadata ?? undefined,
                            periodAbs: dataRow.value.periodAbs
                          }
                        ]}
                        presentation='nested'
                        stickyHeader={false}
                      />
                    </DataTableDetailPanel>
                  ) : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </section>
    </TooltipProvider>
  );
};

export default MultiGroupTableTab;
